import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle, ArrowRight, Diamond, Award, Sparkles, Shield } from 'lucide-react';

export const DimantaGredzeniRiga = () => {
  return (
    <>
      <Helmet>
        <title>Dimanta gredzeni Rīgā | Gredzeni ar dimantiem | Pure Atelier</title>
        <meta name="description" content="Dimanta gredzeni Rīgā ar GIA sertificētiem dimantiem. Plašs klāsts, labākās cenas, individuāls dizains. Pure Atelier - jūsu juvelieris Rīgā." />
        <meta name="keywords" content="dimanta gredzeni, gredzeni ar dimantiem, dimanta gredzeni Rīgā, diamond rings Riga" />
        <link rel="canonical" href="https://pureatelier.lv/dimanta-gredzeni-riga" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/2735970/pexels-photo-2735970.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
                Pure Atelier Riga
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 mb-6 leading-tight">
                Dimanta gredzeni Rīgā
              </h1>
              <p className="text-xl text-stone-600 mb-8 max-w-2xl mx-auto">
                Izsmalcināti gredzeni ar dabīgiem dimantiem. Katra dimanta kvalitāte apstiprināta ar starptautisku sertifikātu.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/gallery?category=rings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Skatīt gredzenu kolekciju <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/37128448991?text=Sveiki!%20Interesē%20dimanta%20gredzeni." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50 px-8">
                    <MessageCircle className="mr-2 h-5 w-5" /> Bezmaksas konsultācija
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Diamond, title: "GIA/IGI sertifikāti", desc: "Starptautiski atzīti" },
                { icon: Award, title: "Roku darbs", desc: "Izgatavots Latvijā" },
                { icon: Sparkles, title: "Dabīgi dimanti", desc: "Augstākā kvalitāte" },
                { icon: Shield, title: "Mūža garantija", desc: "Uz visiem izstrādājumiem" }
              ].map((item, i) => (
                <div key={i} className="text-center p-6">
                  <item.icon className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-stone-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">
                Dimanta gredzeni no Pure Atelier
              </h2>
              
              <div className="prose prose-lg max-w-none text-stone-700">
                <p>
                  <strong>Pure Atelier</strong> piedāvā plašu dimanta gredzenu klāstu Rīgā. Mūsu kolekcijā atradīsiet 
                  gan klasiskus solitaire gredzenus, gan modernākus dizainus ar papildu dimantiem.
                </p>
                
                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Kāpēc izvēlēties mūs?</h3>
                <ul>
                  <li><strong>Sertificēti dimanti</strong> — katrs dimants nāk ar GIA vai IGI sertifikātu</li>
                  <li><strong>Konkurētspējīgas cenas</strong> — tiešā sadarbība bez starpniekiem</li>
                  <li><strong>Plašs izmēru klāsts</strong> — no 0.30ct līdz 2ct un vairāk</li>
                  <li><strong>Dažādi metāli</strong> — baltais zelts, dzeltenais zelts, rozā zelts, platīns</li>
                </ul>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Dimanta kvalitātes kritēriji (4C)</h3>
                <p>
                  Izvēloties dimantu, mēs vadāmies pēc 4C sistēmas: <strong>Cut</strong> (slīpējums), 
                  <strong>Color</strong> (krāsa), <strong>Clarity</strong> (tīrība) un <strong>Carat</strong> (svars). 
                  Mūsu speciālisti palīdzēs izvēlēties optimālu kvalitātes un cenas attiecību.
                </p>
              </div>

              <div className="mt-12 text-center">
                <Link to="/gallery?category=rings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Apskatīt visus dimanta gredzenus <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-stone-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-serif mb-4">Meklējat perfektu dimanta gredzenu?</h2>
            <p className="text-stone-300 mb-8 max-w-2xl mx-auto">
              Sazināties ar mums, lai saņemtu personalizētu piedāvājumu un bezmaksas konsultāciju.
            </p>
            <a href="https://wa.me/37128448991?text=Sveiki!%20Interesē%20dimanta%20gredzeni." target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8">
                <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp konsultācija
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default DimantaGredzeniRiga;
